import { toObject } from '@m1/geo-utils';
import OlMap from '@m1/ol-map';
import * as turf from '@turf/turf';
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { Modify, Translate } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import actions from '@/actions';
import { FENCE_POLYGON_AREA_MIN } from '@/config';
import { MISSION_STYLES } from '@/define/missionStyles';
import { NotifierService as notifier } from '@/libs/Notifier';

const FencePolygon = ({ index, data }) => {
  const dispatch = useDispatch();
  const map = OlMap.getMap();

  const originCoords = useMemo(() => {
    return data.boundary.map(({ lat, lng }) => fromLonLat([lng, lat]));
  }, [data.boundary]);

  useEffect(() => {
    const feature = new Feature(new Polygon([originCoords]));
    const source = new VectorSource({ features: [feature], wrapX: false });
    const layer = new VectorLayer({
      source,
      style: MISSION_STYLES.FENCE.POLYGON.OUTLINE,
    });
    map.addLayer(layer);

    const translate = new Translate({ features: new Collection([feature]) });
    translate.on('translateend', editPolygon);
    map.addInteraction(translate);

    const modify = new Modify({ source });
    modify.on('modifyend', editPolygon);
    map.addInteraction(modify);

    return () => {
      source.clear();
      map.removeLayer(layer);
      map.removeInteraction(modify);
      map.removeInteraction(translate);
    };
  }, [originCoords]);

  const editPolygon = (e) => {
    const coords = e.features.item(0).getGeometry().getCoordinates()[0];
    const currShape = turf.polygon([coords.map((coord) => toLonLat(coord))]);
    const prevShape = turf.polygon([originCoords.map((coord) => toLonLat(coord))]);

    // 동일한 모양인 경우
    if (turf.booleanEqual(currShape, prevShape)) return;

    const selfIntersections = turf.kinks(currShape).features;
    // 자기교차점 존재 시
    if (selfIntersections.length > 0) {
      handleInvalidAction(e, 'The fence polygon cannot have self-intersections. Please retry.');
      return;
    }

    const area = turf.area(currShape);
    // 면적 100m² 미만 시
    if (area < FENCE_POLYGON_AREA_MIN) {
      handleInvalidAction(e, `The fence area cannot be smaller than ${FENCE_POLYGON_AREA_MIN}m². Please retry.`);
      return;
    }

    const boundary = coords.map((coord) => {
      return toObject(toLonLat(coord));
    });

    dispatch(actions.mission.mavlink.editFence(index, { boundary }));
  };

  const handleInvalidAction = (e, message) => {
    e.features.item(0).getGeometry().setCoordinates([originCoords]);
    notifier.error(message);
  };

  return null;
};

export default FencePolygon;
